import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';

/**
 * AnnouncementTicker — announcement card section for the homepage.
 *
 * Design principles (elderly-friendly):
 * - Large, readable text (15px+)
 * - Flyer/image shown directly — no click required to see it
 * - Slow auto-rotate (8s per announcement)
 * - Simple tap to read more — no swipe gestures
 * - High contrast, theme-aware colors
 * - Section heading for clear context
 */

const ROTATION_INTERVAL = 8000;

const categoryIcons = {
  general: '📢', event: '🕌', community: '🤝', fundraising: '💰',
  urgent: '🚨', education: '📚', volunteer: '🙋', default: '📢',
};

const AnnouncementTicker = ({ announcements = [], theme, isMobile }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [paused, setPaused] = useState(false);
  const [animating, setAnimating] = useState(false);

  const active = announcements.filter(a => {
    if (a.expires_at && new Date(a.expires_at) < new Date()) return false;
    return true;
  });

  useEffect(() => {
    if (active.length <= 1 || paused || expanded) return;
    const timer = setInterval(() => {
      setAnimating(true);
      setTimeout(() => {
        setCurrentIndex(prev => (prev + 1) % active.length);
        setAnimating(false);
      }, 300);
    }, ROTATION_INTERVAL);
    return () => clearInterval(timer);
  }, [active.length, paused, expanded]);

  useEffect(() => { setCurrentIndex(0); }, [announcements.length]);

  const handleExpand = useCallback(() => { setExpanded(true); setPaused(true); }, []);
  const handleClose = useCallback(() => { setExpanded(false); setPaused(false); }, []);

  if (active.length === 0) return null;

  const current = active[currentIndex] || active[0];
  const icon = categoryIcons[current.category?.toLowerCase()] || categoryIcons.default;
  const primaryColor = theme?.colors?.primary || '#1a5632';
  const headingColor = theme?.colors?.headingColor || theme?.colors?.primary || '#0f172a';
  const linkColor = theme?.colors?.linkColor || primaryColor;
  const textColor = theme?.colors?.text || theme?.colors?.cardText || 'inherit';
  const secondaryColor = theme?.colors?.darkGray || theme?.colors?.cardTextSecondary || '#666';

  const getRelativeTime = (date) => {
    if (!date) return '';
    const diff = Date.now() - new Date(date).getTime();
    const hours = Math.floor(diff / 3600000);
    if (hours < 1) return 'Just now';
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <>
      {/* Section container */}
      <div style={{ marginBottom: '24px' }}>
        {/* Section Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '12px',
        }}>
          <h2 style={{
            fontSize: isMobile ? '20px' : '24px',
            fontWeight: '600',
            margin: 0,
            color: headingColor,
          }}>
            Announcements
          </h2>
          <Link
            to="/community/announcements"
            style={{
              color: linkColor,
              textDecoration: 'none',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            View All →
          </Link>
        </div>

        {/* Announcement Card — shows image directly */}
        <div
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => !expanded && setPaused(false)}
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            border: '1px solid rgba(0,0,0,0.06)',
            opacity: animating ? 0 : 1,
            transform: animating ? 'translateY(-4px)' : 'translateY(0)',
            transition: 'opacity 0.3s ease, transform 0.3s ease',
          }}
        >
          {/* Image — shown directly, full width */}
          {current.image_url && (
            <img
              src={current.image_url}
              alt={current.title}
              style={{
                width: '100%',
                maxHeight: isMobile ? '200px' : '240px',
                objectFit: 'cover',
                display: 'block',
              }}
            />
          )}

          {/* Card body */}
          <div style={{ padding: isMobile ? '14px' : '16px 20px' }}>
            {/* Title row */}
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px',
              marginBottom: '8px',
            }}>
              <span style={{ fontSize: '20px', lineHeight: 1 }}>{icon}</span>
              <div style={{ flex: 1 }}>
                <h3 style={{
                  margin: 0,
                  fontSize: isMobile ? '15px' : '16px',
                  fontWeight: '700',
                  color: '#1e293b',
                  lineHeight: 1.3,
                }}>
                  {current.title}
                </h3>
                {/* Meta: category + time */}
                <div style={{
                  display: 'flex',
                  gap: '8px',
                  alignItems: 'center',
                  marginTop: '4px',
                  flexWrap: 'wrap',
                }}>
                  {current.category && (
                    <span style={{
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: '600',
                      backgroundColor: `${primaryColor}10`,
                      color: primaryColor,
                    }}>
                      {current.category}
                    </span>
                  )}
                  {current.is_pinned && (
                    <span style={{ fontSize: '11px', color: '#92400e', fontWeight: '600' }}>
                      📌 Pinned
                    </span>
                  )}
                  <span style={{ fontSize: '12px', color: '#94a3b8' }}>
                    {getRelativeTime(current.published_at || current.created_at)}
                  </span>
                </div>
              </div>
            </div>

            {/* Preview text — 2 lines max, tap for more */}
            {current.content && (
              <div
                style={{
                  fontSize: '14px',
                  lineHeight: '1.6',
                  color: '#475569',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  marginBottom: '10px',
                }}
                dangerouslySetInnerHTML={{ __html: current.content }}
                className="announcement-preview"
              />
            )}

            {/* Read More + dots row */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <button
                onClick={handleExpand}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '6px 0',
                  color: linkColor,
                  fontWeight: '600',
                  fontSize: '14px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  minHeight: '44px', // Elderly-friendly tap target
                }}
              >
                Read More
                <svg width="14" height="14" viewBox="0 0 24 24" fill={linkColor}>
                  <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/>
                </svg>
              </button>

              {/* Dot indicators */}
              {active.length > 1 && (
                <div style={{ display: 'flex', gap: '5px' }}>
                  {active.map((_, i) => (
                    <div
                      key={i}
                      onClick={() => { setCurrentIndex(i); setPaused(true); }}
                      style={{
                        width: i === currentIndex ? '18px' : '8px',
                        height: '8px',
                        borderRadius: '4px',
                        backgroundColor: i === currentIndex ? primaryColor : '#d1d5db',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Expanded modal */}
      {expanded && (
        <div
          onClick={handleClose}
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              maxWidth: '500px',
              width: '100%',
              maxHeight: '85vh',
              overflow: 'auto',
              boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
              animation: 'slideUp 0.3s ease',
            }}
          >
            {/* Close button */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px 20px 12px',
            }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#1e293b' }}>
                {icon} {current.title}
              </h3>
              <button
                onClick={handleClose}
                style={{
                  width: '40px', height: '40px',
                  borderRadius: '50%', border: 'none',
                  backgroundColor: '#f1f5f9', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '20px', color: '#64748b', flexShrink: 0,
                }}
              >
                ✕
              </button>
            </div>

            {/* Full image */}
            {current.image_url && (
              <img
                src={current.image_url}
                alt={current.title}
                style={{
                  width: '100%',
                  maxHeight: '400px',
                  objectFit: 'contain',
                  backgroundColor: '#f8fafc',
                }}
              />
            )}

            {/* Full content */}
            <div style={{ padding: '16px 20px' }}>
              <div style={{
                display: 'flex', gap: '8px', alignItems: 'center',
                marginBottom: '12px', flexWrap: 'wrap',
              }}>
                {current.category && (
                  <span style={{
                    padding: '4px 10px', borderRadius: '20px', fontSize: '12px',
                    fontWeight: '600', backgroundColor: `${primaryColor}10`, color: primaryColor,
                  }}>
                    {current.category}
                  </span>
                )}
                {current.is_pinned && (
                  <span style={{
                    padding: '4px 10px', borderRadius: '20px', fontSize: '12px',
                    fontWeight: '600', backgroundColor: '#fef3c7', color: '#92400e',
                  }}>
                    📌 Pinned
                  </span>
                )}
                <span style={{ fontSize: '13px', color: '#94a3b8' }}>
                  {getRelativeTime(current.published_at || current.created_at)}
                </span>
              </div>

              <div
                style={{ fontSize: '15px', lineHeight: '1.7', color: '#334155' }}
                dangerouslySetInnerHTML={{ __html: current.content || '<p>No additional details.</p>' }}
                className="announcement-content"
              />
              <style>{`
                .announcement-content p { margin: 0 0 8px 0; }
                .announcement-content p:last-child { margin-bottom: 0; }
                .announcement-content a { color: ${primaryColor}; text-decoration: underline; word-break: break-word; }
                .announcement-content ul, .announcement-content ol { margin: 8px 0; padding-left: 20px; }
                .announcement-content li { margin-bottom: 4px; }
                .announcement-preview p { margin: 0; display: inline; }
                .announcement-preview br { display: none; }
              `}</style>

              {current.sponsor_enabled && current.payment_link_url && (
                <a
                  href={current.payment_link_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'block', marginTop: '16px', padding: '14px',
                    borderRadius: '10px', backgroundColor: `${primaryColor}08`,
                    border: `1px solid ${primaryColor}20`, textAlign: 'center',
                    textDecoration: 'none', color: primaryColor, fontWeight: '600', fontSize: '15px',
                  }}
                >
                  {current.sponsor_message || 'Support This Cause'}
                </a>
              )}
            </div>

            <div style={{
              padding: '12px 20px 20px', borderTop: '1px solid #f1f5f9', textAlign: 'center',
            }}>
              <Link
                to="/community/announcements"
                onClick={handleClose}
                style={{ color: primaryColor, textDecoration: 'none', fontSize: '14px', fontWeight: '600' }}
              >
                View All Announcements →
              </Link>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
};

export default AnnouncementTicker;
